import React from 'react';
import { useLocation } from 'react-router-dom';
import { Loader2, ShieldAlert } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useSignInDialog } from '../context/SignInDialogContext';
import Navbar from './Navbar';
import Footer from './Footer';
import { Button } from './ui/button';

/**
 * Guards a route by sign-in status and (optionally) by role.
 * @param {string} role - 'employee' | 'partner' (omit to require any signed-in user)
 */
const ProtectedRoute = ({ children, role }) => {
  const { user, loading } = useAuth();
  const { open: openSignIn } = useSignInDialog();
  // location is currently unused; useLocation hook kept for future use cases.
  useLocation();

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <Loader2 className="w-7 h-7 text-blue-400 animate-spin" />
      </div>
    );
  }

  if (!user) {
    return (
      <div className="bg-black min-h-screen">
        <Navbar />
        <main className="max-w-[760px] mx-auto px-6 py-24 text-center">
          <div className="w-14 h-14 rounded-full bg-blue-500/15 border border-blue-500/40 mx-auto flex items-center justify-center">
            <ShieldAlert className="w-7 h-7 text-blue-400" />
          </div>
          <h1 className="mt-6 text-[32px] sm:text-[40px] leading-tight font-semibold text-white tracking-[-0.02em]">
            Please sign in to continue
          </h1>
          <p className="mt-4 text-[15px] text-neutral-400 leading-relaxed max-w-md mx-auto">
            This area is for verified MetaxuOne {role === 'employee' ? 'employees' : role === 'partner' ? 'partners' : 'users'}.
            Sign in with your company email to continue.
          </p>
          <div className="mt-7 flex flex-wrap gap-3 justify-center">
            <Button
              onClick={() => openSignIn('signin')}
              className="h-12 px-7 rounded-full bg-white hover:bg-neutral-200 text-neutral-950 text-[14px] font-medium"
            >
              Sign in
            </Button>
            <Button
              onClick={() => openSignIn('signup')}
              variant="outline"
              className="h-12 px-7 rounded-full border-neutral-700 bg-transparent text-white hover:bg-neutral-900 text-[14px] font-medium"
            >
              Create account
            </Button>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  if (role && user.role !== role) {
    const altPortal = user.role === 'employee' ? '/employee-portal' : user.role === 'partner' ? '/partner-portal' : null;
    return (
      <div className="bg-black min-h-screen">
        <Navbar />
        <main className="max-w-[760px] mx-auto px-6 py-24 text-center">
          <div className="w-14 h-14 rounded-full bg-amber-500/15 border border-amber-500/40 mx-auto flex items-center justify-center">
            <ShieldAlert className="w-7 h-7 text-amber-400" />
          </div>
          <h1 className="mt-6 text-[32px] sm:text-[40px] leading-tight font-semibold text-white tracking-[-0.02em]">
            Access restricted
          </h1>
          <p className="mt-4 text-[15px] text-neutral-400 leading-relaxed max-w-md mx-auto">
            You&rsquo;re signed in as <span className="text-white">{user.email}</span> (<span className="capitalize">{user.role}</span>), which doesn&rsquo;t have access to the
            {' '}{role === 'employee' ? 'Employee' : 'Partner'} Portal.
            {altPortal && ' We\u2019ve linked your accessible portal below.'}
          </p>
          {altPortal && (
            <Button
              onClick={() => (window.location.href = altPortal)}
              className="mt-7 h-12 px-7 rounded-full bg-white hover:bg-neutral-200 text-neutral-950 text-[14px] font-medium"
            >
              Go to my portal
            </Button>
          )}
        </main>
        <Footer />
      </div>
    );
  }

  return children;
};

export default ProtectedRoute;
